import { useMemo } from 'react';

import ConversionNumber from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import numberFormatter from 'bloko/common/numberFormatter';
import { formatToReactComponent } from 'bloko/common/trl';

import ContextTitleWithVacancy from 'src/components/Search/Resume/ContextVacancy/TitleWithVacancy';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import { ContextViewMode } from 'src/models/resumeSearchContext';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

import TitleWrapper from 'src/components/Search/Resume/Header/Title/Wrapper';

const TrlKeys = {
    shortHeader: 'search.resume.header.short',
    found: 'employer.resumesSearch.found',
    suitableZero: 'resume.search.suitable.zero',
    suitableOne: 'resume.search.suitable.one',
    suitableMany: 'resume.search.suitable.many',
    resumes: 'employer.resumesSearch.resumes',
};

const ResumeSearchTitle: TranslatedComponent = ({ trls }) => {
    const isSimilar = useSelector((state) => state.resumeSearchResult.isSimilarSearch);
    const stats = useSelector((state) => state.resumeSearchResult.stats);

    const { resumesCount, content } = useMemo(() => {
        if (isSimilar) {
            if (stats.totalUngroupedResults === 0) {
                return { resumesCount: 0, content: trls[TrlKeys.suitableZero] };
            }

            const resumesCount = stats.totalUngroupedResults;
            return {
                resumesCount,
                content: formatToReactComponent(trls[TrlKeys.shortHeader], {
                    '{0}': (
                        <ConversionNumber
                            hasValue
                            formatValue={(value) =>
                                numberFormatter.format(`${value}`, { groupSeparator: NON_BREAKING_SPACE })
                            }
                            value={resumesCount}
                            one={trls[TrlKeys.suitableOne]}
                            some={trls[TrlKeys.suitableMany]}
                            many={trls[TrlKeys.suitableMany]}
                        />
                    ),
                }),
            };
        }

        if (
            stats.normalSearchResults === 0 ||
            (!stats.normalGroupedSearchResults && stats.totalUngroupedResults === 0)
        ) {
            return { resumesCount: 0, content: `${trls[TrlKeys.found]}0 ${trls[TrlKeys.resumes]}` };
        }

        const resumesCount = stats.normalGroupedSearchResults ? stats.normalSearchResults : stats.totalUngroupedResults;
        return {
            resumesCount,
            content: formatToReactComponent(trls[TrlKeys.shortHeader], {
                '{0}': numberFormatter.format(resumesCount.toString(), { groupSeparator: NON_BREAKING_SPACE }),
            }),
        };
    }, [isSimilar, stats, trls]);

    const contextViewMode = useSelector((state) => state.resumeSearchContext?.viewMode);
    const contextVacancy = useSelector((state) => state.resumeSearchContext?.vacancy);
    if (contextViewMode === ContextViewMode.Title && contextVacancy) {
        return (
            <ContextTitleWithVacancy
                resumesCount={resumesCount}
                vacancyName={contextVacancy.name}
                vacancyId={contextVacancy.id}
                filters={contextVacancy.filters}
            />
        );
    }

    return <TitleWrapper>{content}</TitleWrapper>;
};

export default translation(ResumeSearchTitle);
